import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import PageNavigation from '../../../components/PageNavigation/PageNavigation'
import registry1 from '../../../assets/Registry/registry1.png'
import registry2 from '../../../assets/Registry/registry2.png'
import registry3 from '../../../assets/Registry/registry3.png'
import registry4 from '../../../assets/Registry/registry4.png'
import './Registry.css'

const Registry = () => {
  const [activeStep, setActiveStep] = useState(1)
  const [lightboxImage, setLightboxImage] = useState(null)
  const [lightboxAlt, setLightboxAlt] = useState('')

  const openLightbox = (imageSrc, altText) => {
    setLightboxImage(imageSrc)
    setLightboxAlt(altText)
    document.body.style.overflow = 'hidden'
  }

  const closeLightbox = () => {
    setLightboxImage(null)
    setLightboxAlt('')
    document.body.style.overflow = ''
  }

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && lightboxImage) {
        closeLightbox()
      }
    }
    if (lightboxImage) {
      window.addEventListener('keydown', handleEscape)
    }
    return () => {
      window.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [lightboxImage])

  const steps = [
    {
      id: 1,
      title: 'Create a New Registry',
      description: 'Start by creating your first container registry in Aedify.AI.',
      image: registry1,
      details: 'Navigate to the Registry section from the main dashboard. Click the "+ Create Registry" button in the top right corner. Aedify.AI will automatically create a registry for you with a unique domain (e.g., deployment.aedify.ai). Your registry is ready to use immediately after creation.'
    },
    {
      id: 2,
      title: 'View Docker Commands',
      description: 'Get the Docker commands needed to push and pull images.',
      image: registry3,
      hasImageRight: true,
      details: 'After selecting an image, click the "> Docker Commands" button in the image details panel. This will show you step-by-step Docker commands. You can copy each command using the copy button next to it.'
    },
    {
      id: 3,
      title: 'Create API Keys',
      description: 'Generate API keys for Docker authentication.',
      details: (
        <>
          To authenticate with the Docker registry, you'll need an API key. Navigate to the{' '}
          <Link to="/resources/api-keys" className="inline-link">API Keys page</Link> to create a new API key.
          <br /><br />
          Once you have your API key, use it with your email address to log in to the Docker registry:
          <pre><code>docker login deployment.aedify.ai -u &lt;your-email&gt; -p &lt;your-api-key&gt;</code></pre>
          Keep your API key secure and never share it publicly. API keys provide full access to your registry.
        </>
      ),
      hasApiKeyLink: true
    },
    {
      id: 4,
      title: 'View Registry and Images',
      description: 'Browse your registry and manage container images.',
      image: registry4,
      details: 'Once your registry is created, you can see it listed in the left sidebar. Select a registry to view all your container images. Each image shows its name, last update time, and number of tags. You can search for specific images or tags using the search bar. Click on an image to view its details, including available tags and Docker commands.'
    }
  ]

  useEffect(() => {
    const handleScroll = () => {
      const allStepIds = steps.map(s => ({ id: `registry-step-${s.id}`, stepId: s.id }))
      const scrollPosition = window.scrollY + 200

      for (let i = allStepIds.length - 1; i >= 0; i--) {
        const element = document.getElementById(allStepIds[i].id)
        if (element && element.offsetTop <= scrollPosition) {
          setActiveStep(allStepIds[i].stepId)
          break
        }
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [steps])

  return (
    <div className="registry-page">
      <div className="registry-content-wrapper">
        <div className="registry-main-content">
      <h1>Registry</h1>
      <p className="intro">
        Manage container images with Aedify.AI's integrated container registry. Push, pull, and 
        manage your Docker images in one place.
      </p>

          <section className="registry-section">
            <h2>Getting Started with Registry</h2>
            <p className="section-intro">
              Follow these steps to create your registry, manage images, and use Docker commands to push and pull images.
            </p>

            {steps.map((step) => (
              <div 
                key={step.id} 
                className={`step-container ${activeStep === step.id ? 'active' : ''}`}
                id={`registry-step-${step.id}`}
              >
                <div className="step-header">
                  <div className="step-number">{step.id}</div>
                  <h3>{step.title}</h3>
                </div>
                <p className="step-description">{step.description}</p>
                {step.hasImageRight ? (
                  <div className="step-image-right-content">
                    <div className="image-right-details">
                      <div className="step-details">
                        {typeof step.details === 'string' ? <p>{step.details}</p> : step.details}
                      </div>
                    </div>
                    {step.image && (
                      <div className="image-right-wrapper">
                        <div className="step-image image-right-image">
                          <img 
                            src={step.image} 
                            alt={`Step ${step.id}: ${step.title}`}
                            onClick={() => openLightbox(step.image, `Step ${step.id}: ${step.title}`)}
                            className="clickable-image"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    {step.image && (
                      <div className="step-image">
                        <img 
                          src={step.image} 
                          alt={`Step ${step.id}: ${step.title}`}
                          onClick={() => openLightbox(step.image, `Step ${step.id}: ${step.title}`)}
                          className="clickable-image"
                        />
                      </div>
                    )}
                    <div className="step-details">
                      {typeof step.details === 'string' ? <p>{step.details}</p> : step.details}
                    </div>
                  </>
                )}
              </div>
            ))}
      </section>

          <PageNavigation />
        </div>

        {/* Right Sidebar - Step Navigation */}
        <aside className="registry-sidebar">
          <div className="sidebar-section">
            <h3>Getting Started</h3>
            <nav className="step-nav">
              {steps.map((step) => (
                <a
                  key={step.id}
                  href={`#registry-step-${step.id}`}
                  className={`step-nav-item ${activeStep === step.id ? 'active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault()
                    setActiveStep(step.id)
                    document.getElementById(`registry-step-${step.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }}
                >
                  <span className="step-nav-number">{step.id}</span>
                  <span className="step-nav-title">{step.title}</span>
                </a>
              ))}
            </nav>
          </div>

          <div className="sidebar-section tips-section">
            <h3>Tips</h3>
            <ul className="tips-list">
              <li>Use descriptive image names that reflect the application or service</li>
              <li>Tag images with version numbers or commit hashes for better tracking</li>
              <li>Regularly clean up unused images to manage storage costs</li>
              <li>Keep your API keys secure and rotate them periodically</li>
              <li>Use the Docker Commands panel for quick access to commands</li>
            </ul>
          </div>
        </aside>
      </div>

      {lightboxImage && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox} aria-label="Close lightbox">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <img src={lightboxImage} alt={lightboxAlt} className="lightbox-image" />
          </div>
        </div>
      )}
    </div>
  )
}

export default Registry
